<script setup>
import { ref, onMounted, computed } from 'vue'
import { i18n } from '@/i18n'

const emit = defineEmits(['finish'])

const STORAGE_PREFIX = 'v1_hint_seen_'
const isVisible = ref(false)
const activeHint = ref(null)

const hints = [
  {
    id: 'reserved_header',
    target: '#tour-reserved-header',
    title: i18n.t('productAvailability.tour_reserved_header_title'),
    content: i18n.t('productAvailability.tour_reserved_header_content'),
    placement: 'bottom',
  },
  {
    id: 'reserved_edit',
    target: '.reserved-stock-edit',
    title: i18n.t('productAvailability.tour_reserved_edit_title'),
    content: i18n.t('productAvailability.tour_reserved_edit_content'),
    placement: 'right',
  },
  {
    id: 'reserved_bulk_edit',
    target: '#tour-bulk-reserved',
    title: i18n.t('productAvailability.tour_bulk_reserved_title'),
    content: i18n.t('productAvailability.tour_bulk_reserved_content'),
    placement: 'bottomRight',
  },
]

// Tracking which hints have been seen
const seenHints = ref({})

function loadSeenStatus() {
  hints.forEach(hint => {
    seenHints.value[hint.id] = localStorage.getItem(STORAGE_PREFIX + hint.id) === 'true'
  })
}

function isElementInViewport(el) {
  const rect = el.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

function updateQueue() {
  if (activeHint.value) {
    const el = document.querySelector(activeHint.value.target)
    if (el) {
      activeHint.value.rect = el.getBoundingClientRect()
    }
    return
  }

  for (const hint of hints) {
    if (!seenHints.value[hint.id]) {
      const el = document.querySelector(hint.target)
      if (el && isElementInViewport(el)) {
        const rect = el.getBoundingClientRect()
        if (rect.width > 0 || rect.height > 0) {
          activeHint.value = { ...hint, rect }
          isVisible.value = true
          break
        }
      }
    }
  }
}

onMounted(() => {
  loadSeenStatus()
  
  const interval = setInterval(() => {
    updateQueue()
  }, 500)

  setTimeout(() => clearInterval(interval), 120000)
})

function handleDismiss() {
  if (activeHint.value) {
    const id = activeHint.value.id
    localStorage.setItem(STORAGE_PREFIX + id, 'true')
    seenHints.value[id] = true
    
    activeHint.value = null
    isVisible.value = false
  }
}

const hasActiveTargets = computed(() => !!activeHint.value)

const anchorStyle = computed(() => {
  if (!activeHint.value) return { display: 'none' }
  const { rect } = activeHint.value
  return {
    position: 'fixed',
    top: `${rect.top + rect.height / 2}px`,
    left: `${rect.left + rect.width / 2}px`,
    width: '1px',
    height: '1px',
    zIndex: 9999,
    pointerEvents: 'none',
  }
})

const highlightStyle = computed(() => {
  if (!activeHint.value) return { display: 'none' }
  const { rect } = activeHint.value
  return {
    position: 'fixed',
    top: `${rect.top - 4}px`,
    left: `${rect.left - 4}px`,
    width: `${rect.width + 8}px`,
    height: `${rect.height + 8}px`,
    zIndex: 9998,
    pointerEvents: 'none',
  }
})
</script>

<template>
  <div v-if="isVisible && hasActiveTargets" class="inventory-tour-container">
    <div class="inventory-tour-overlay" />
    
    <div class="tour-target-highlight" :style="highlightStyle" />

    <a-popover
      :visible="isVisible"
      :placement="activeHint.placement"
      trigger="manual"
      overlay-class-name="inventory-tour-popover"
    >
      <template slot="title">
        <span class="tour-title text-danger">{{ activeHint.title }}</span>
      </template>
      
      <template slot="content">
        <div class="tour-content">
          <p>{{ activeHint.content }}</p>
          <div class="tour-footer">
            <a-button type="primary" size="small" @click="handleDismiss">
              {{ $t('productAvailability.tour_paham') }}
            </a-button>
          </div>
        </div>
      </template>
      
      <div class="tour-anchor" :style="anchorStyle" />
    </a-popover>
  </div>
</template>

<style lang="scss">
.inventory-tour-container {
  position: relative;
  z-index: 9997;
}

.inventory-tour-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9997;
  pointer-events: none;
}

.inventory-tour-popover {
  z-index: 10000 !important;
  pointer-events: auto !important;
}

.tour-title {
  font-weight: 600;
}

.tour-content {
  max-width: 260px;
  padding: 4px 0;
  p {
    margin-bottom: 16px;
    font-size: 13px;
    line-height: 1.6;
    color: #595959;
  }
}

.tour-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
  margin-top: 8px;
}

.tour-target-highlight {
  border: 2px solid #ff4d4f;
  border-radius: 4px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.4);
  pointer-events: none;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border: 2px solid #ff4d4f;
    border-radius: 6px;
    opacity: 0;
    animation: tour-pulse 2s infinite;
  }
}

@keyframes tour-pulse {
  0% { transform: scale(1); opacity: 0.5; }
  100% { transform: scale(1.3); opacity: 0; }
}

.tour-anchor {
  pointer-events: none;
}
</style>
